
import { computed, defineComponent, PropType, reactive, toRefs } from "vue";
import { LayerControl, MapLayerGroup } from "@/types";
import { last } from "lodash-es";
import mapboxgl from "mapbox-gl";
import { useMap } from "@/services";

interface LocalState {
  savedOpacity: number;
  layerGroups: MapLayerGroup[];
  ddOptions: { value: string; text: string }[];
  opacity: number;
  isVisible: boolean;
  isEditable: boolean;
  name: string;
  visiblePath: string;
  layerDefinition: Partial<mapboxgl.Layer>;
  zoomLevel: number;
  isDisabled: boolean;
  zoomWarning: string;
  activeLayerId: string;
}

export default defineComponent({
  name: "MapLayerController",
  props: {
    layerControl: {
      type: Object as PropType<LayerControl>,
      required: true,
    },
  },
  emits: ["setLayerId"],
  setup(props, { emit }) {
    const state = reactive({
      savedOpacity: 0,
      ddOptions: computed(() =>
        props.layerControl?.isStyleLayer
          ? props.layerControl.allLayerIds.map((id: string) => ({
              value: id,
              text: layerName(id),
            }))
          : []
      ),
      layerGroups: computed(() => useMap().layerGroups),
      opacity: computed({
        get(): number {
          return props.layerControl.opacity;
        },
        set(val: number) {
          update("opacity", val);
        },
      }),
      isVisible: computed({
        get(): boolean {
          return props.layerControl.isVisible;
        },
        set(val: boolean) {
          update("isVisible", val);
        },
      }),
      activeLayerId: computed({
        get() {
          return props.layerControl.activeLayerId;
        },
        set(val: string) {
          emit("setLayerId", val);
        },
      }),
      name: computed(() => props.layerControl.parentGroup?.name || ""),
      isEditable: computed(() => props.layerControl.isEditable),
      visiblePath: computed(() =>
        props.layerControl.isStyleLayer
          ? [...props.layerControl.parentGroups]
              .reverse()
              .map((g) => g.name)
              .join(" / ")
          : last(props.layerControl.parentGroups)?.name || ""
      ),
      layerDefinition: computed(() => {
        return (
          (useMap().mapSettings?.layers || []).find((x) =>
            props.layerControl.layerIds.some((y) => y === x.id)
          ) || {}
        );
      }),
      zoomLevel: computed(() => useMap().mapPosition.zoom),
      isDisabled: computed(() => {
        const { minzoom, maxzoom } = state.layerDefinition;
        return (
          (minzoom ? state.zoomLevel <= minzoom : false) ||
          (maxzoom ? state.zoomLevel >= maxzoom : false)
        );
      }),
      zoomWarning: computed(() =>
        state.isDisabled
          ? `Zoom ${
              state.layerDefinition.minzoom &&
              state.zoomLevel <= state.layerDefinition.minzoom
                ? "in"
                : "out"
            } to display on map`
          : ""
      ),
    }) as LocalState;
    const update = (key: "isVisible" | "opacity", value: unknown) => {
      useMap().updateLayerGroup({
        path: props.layerControl?.parentGroup?.path || "",
        key,
        value,
      });
    };
    const layerName = (id: string) =>
      state.layerGroups.find((l: any) => l.layers.includes(id))?.name || id;
    const toggleOpacity = () => {
      state.opacity = state.opacity ? 0 : state.savedOpacity;
    };

    const editLayer = () => {
      if (props.layerControl.parentGroup && props.layerControl.isEditable) {
        useMap().setEditLayer(
          props.layerControl.parentGroup.layers[0] as string,
          {
            isFocusMode: true,
          }
        );
      }
    };
    return {
      ...toRefs(state),
      sliderOptions: {
        min: 0,
        max: 1,
        interval: 0.05,
      },
      toggleOpacity,
      editLayer,
    };
  },
  watch: {
    opacity: {
      handler(newVal: number, oldVal: number) {
        if (newVal && newVal !== oldVal) {
          this.savedOpacity = newVal;
        }
      },
      immediate: true,
    },
  },
});
